import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import isTemperatureValid from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/constraint/isTemperatureValid';
import isVocabularyTermsValid from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/constraint/isVocabularyTermsValid';
import splitVocabularyTerms from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/constraint/splitVocabularyTerms';
import type ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import { AbstractValidatedClass, ValidatedClass, constraint } from '@verclam/form-validator';

export type VocabularyBoundsArgs = { readonly maxTerms: number; readonly maxTermLength: number };
export type TemperatureBoundsArgs = { readonly minimum: number; readonly maximum: number };

export type UsageSettingsFormArgs = {
	isVocabularyOverridden: boolean;
	vocabularyTerms: string;
	isContextOverridden: boolean;
	contextPrompt: string;
	isSystemPromptOverridden: boolean;
	systemPrompt: string;
	isTemperatureOverridden: boolean;
	temperature: number | null;
	includesAccountOnlyFields: boolean;
	vocabularyBounds: VocabularyBoundsArgs;
	temperatureBounds: TemperatureBoundsArgs;
};

/**
 * The vocabulary field carries the `TextArea`'s **raw text**, never `string[]`: `TextArea` is
 * strictly controlled, and a `string[]` field would re-split on every keystroke, turning `Entrée`
 * into an immediately-discarded empty line — the user could never type a second term. Splitting
 * happens only in `toCommandData()` and in `isVocabularyTermsValid`; `fromView` joins with `\n` once,
 * at open.
 *
 * Deliberately **not** `ignoreNullValue`: `temperature` can be `null` while `isTemperatureOverridden`
 * is `true`, and whether that combination is valid depends on the level — `temperature` is `editable`
 * **only** at the account level, so `includesAccountOnlyFields` doubles as "is this field
 * writable here". At the account level a `null` simply means "nobody has configured this yet", always
 * valid. At organization/facility level the field is read-only, so this combination can only be
 * *constructed* (a stale/crafted `FormData`), never produced through the UI — it is still rejected
 * defensively rather than silently reputed valid, the same reasoning as `AiModelFormData.family`.
 * `ignoreNullValue` would skip the constraint unconditionally and lose that distinction; instead
 * `isTemperatureValid` reads `includesAccountOnlyFields` itself. Requiredness for every field here is
 * conditional on its own `isXxxOverridden` flag, piloted inside each constraint function, never by
 * `@ValidatedClass` options.
 */
@ValidatedClass()
class UsageSettingsFormData extends AbstractValidatedClass<UsageSettingsFormArgs> {
	public readonly isVocabularyOverridden: boolean;

	@constraint(isVocabularyTermsValid, () => 'Le vocabulaire métier dépasse les limites autorisées.')
	public readonly vocabularyTerms: string;

	public readonly isContextOverridden: boolean;

	public readonly contextPrompt: string;

	public readonly isSystemPromptOverridden: boolean;

	public readonly systemPrompt: string;

	public readonly isTemperatureOverridden: boolean;

	@constraint(isTemperatureValid, () => 'La température doit être comprise dans les bornes autorisées.')
	public readonly temperature: number | null;

	/** Decides the *shape* of the `PUT` body (`toCommandData`), never a field's editability — that is
	 * `editable`, read from the server. The account route always carries `systemPrompt`/`temperature`;
	 * the organization/facility routes never do. Set once, in `fromView`, from
	 * `scope.type === 'account'` — the only read of `scope.type` this class makes. */
	public readonly includesAccountOnlyFields: boolean;

	public readonly vocabularyBounds: VocabularyBoundsArgs;
	public readonly temperatureBounds: TemperatureBoundsArgs;

	constructor(data: UsageSettingsFormArgs) {
		super(data);
		this.isVocabularyOverridden = data.isVocabularyOverridden;
		this.vocabularyTerms = data.vocabularyTerms;
		this.isContextOverridden = data.isContextOverridden;
		this.contextPrompt = data.contextPrompt;
		this.isSystemPromptOverridden = data.isSystemPromptOverridden;
		this.systemPrompt = data.systemPrompt;
		this.isTemperatureOverridden = data.isTemperatureOverridden;
		this.temperature = data.temperature;
		this.includesAccountOnlyFields = data.includesAccountOnlyFields;
		this.vocabularyBounds = data.vocabularyBounds;
		this.temperatureBounds = data.temperatureBounds;
		Object.freeze(this);
	}

	static createEmpty(): UsageSettingsFormData {
		return new UsageSettingsFormData({
			isVocabularyOverridden: false,
			vocabularyTerms: '',
			isContextOverridden: false,
			contextPrompt: '',
			isSystemPromptOverridden: false,
			systemPrompt: '',
			isTemperatureOverridden: false,
			temperature: null,
			includesAccountOnlyFields: false,
			vocabularyBounds: { maxTerms: 0, maxTermLength: 0 },
			temperatureBounds: { minimum: 0, maximum: 0 },
		});
	}

	/**
	 * `own !== null` is "overridden here" (`UsageSettingView.isOverridden`) — the flag the org/facility
	 * `Switch` starts from. At the account level there is nothing above to inherit from, so a *rendered*
	 * field always carries an override: the switch is never shown there. But a setting that is not
	 * `applicable` to this usage has nothing to render and nothing to override — it must still resolve to
	 * `null` in `toCommandData()`, so the flag follows `applicable`, not an unconditional `true`.
	 */
	static fromView(entry: ModelAssignmentView, scope: AssignmentScope): UsageSettingsFormData {
		const { vocabularyTerms, contextPrompt, systemPrompt, temperature } = entry.settings;
		const isAccountLevel = scope.type === 'account';

		return new UsageSettingsFormData({
			isVocabularyOverridden: isAccountLevel
				? vocabularyTerms.applicable
				: vocabularyTerms.isOverridden,
			vocabularyTerms: vocabularyTerms.own !== null ? vocabularyTerms.own.join('\n') : '',
			isContextOverridden: isAccountLevel ? contextPrompt.applicable : contextPrompt.isOverridden,
			contextPrompt: contextPrompt.own ?? '',
			isSystemPromptOverridden: isAccountLevel ? systemPrompt.applicable : systemPrompt.isOverridden,
			systemPrompt: systemPrompt.own ?? '',
			isTemperatureOverridden: isAccountLevel ? temperature.applicable : temperature.isOverridden,
			temperature: temperature.own,
			includesAccountOnlyFields: isAccountLevel,
			vocabularyBounds: vocabularyTerms.bounds,
			temperatureBounds: temperature.bounds,
		});
	}

	setVocabularyTerms(vocabularyTerms: string): this {
		return this.cloneWith({ vocabularyTerms } as Partial<UsageSettingsFormArgs>);
	}

	setIsVocabularyOverridden(isVocabularyOverridden: boolean): this {
		return this.cloneWith({ isVocabularyOverridden } as Partial<UsageSettingsFormArgs>);
	}

	setContextPrompt(contextPrompt: string): this {
		return this.cloneWith({ contextPrompt } as Partial<UsageSettingsFormArgs>);
	}

	setIsContextOverridden(isContextOverridden: boolean): this {
		return this.cloneWith({ isContextOverridden } as Partial<UsageSettingsFormArgs>);
	}

	setSystemPrompt(systemPrompt: string): this {
		return this.cloneWith({ systemPrompt } as Partial<UsageSettingsFormArgs>);
	}

	setIsSystemPromptOverridden(isSystemPromptOverridden: boolean): this {
		return this.cloneWith({ isSystemPromptOverridden } as Partial<UsageSettingsFormArgs>);
	}

	setTemperature(temperature: number | null): this {
		return this.cloneWith({ temperature } as Partial<UsageSettingsFormArgs>);
	}

	setIsTemperatureOverridden(isTemperatureOverridden: boolean): this {
		return this.cloneWith({ isTemperatureOverridden } as Partial<UsageSettingsFormArgs>);
	}

	/**
	 * Emits **every** key of the route's body, always — never a subset. Two keys on
	 * `organizations/…`/`facilities/…`, four on `accounts/…`. A setting not `isXxxOverridden` emits
	 * `null` ("hériter"), never by omission; the vocabulary text is split into terms only here.
	 */
	toCommandData(): Record<string, unknown> {
		const body: Record<string, unknown> = {
			vocabularyTerms: this.isVocabularyOverridden ? splitVocabularyTerms(this.vocabularyTerms) : null,
			contextPrompt: this.isContextOverridden ? this.contextPrompt : null,
		};

		if (this.includesAccountOnlyFields) {
			body.systemPrompt = this.isSystemPromptOverridden ? this.systemPrompt : null;
			body.temperature = this.isTemperatureOverridden ? this.temperature : null;
		}

		return body;
	}
}

export default UsageSettingsFormData;
